/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
/**
 * Use the API in the GenAIAssistantUtils module to manage GenAI functionalities.
 * @module GenAIAssistantUtils
 */
import type { CancelTokenSource } from 'axios';

import { invokeRestApi, getCancelTokenSource } from '../rest-client';

const CREATE_CONVERSATION = 'createConversation';
const SEND_MESSAGE = 'sendMessage';
const GET_CONVERSATIONS = 'getConversations';
const GET_MESSAGES = 'getMessages';
const CREATE_CONVERSATION_FOR_AGENT = 'createConversationForAgent';
const SEND_MESSAGE_FOR_AGENT = 'sendMessageForAgent';
const GET_CONVERSATIONS_FOR_AGENT = 'getConversationsForAgent';
const GET_MESSAGES_FOR_AGENT = 'getMessagesForAgent';
const LIKE_AGENT_MESSAGE = 'likeAgentMessage';
const UNLIKE_AGENT_MESSAGE = 'dislikeAgentMessage';

type Prompt = {
  pxObjClass: string;
  pyInstructions: string;
  pyPrompt: string;
};

type ConversationResponse = {
  data: {
    ID: string;
    response: string;
    suggestedPrompts: Prompt[];
    welcomeMessage?: string;
    initialInstruction: string;
    assistantMode?: string;
    messageID?: string;
    aiGuidedQuestions?: Prompt[];
  };
};

type Attachment = {
  type: string;
  category: string;
  fileName: string;
  ID: string;
};

type SendMessageConfig = {
  enableStreaming: boolean;
};

const processGetConversationsRequest = (
  apiKey: string,
  assistantID: string,
  contextID: string,
  pageSize: number,
  pageIndex: number,
  context: string,
  cancelTokenSource: CancelTokenSource[]
) => {
  // Cancel any ongoing requests from previous CancelTokenSources
  for (const token of cancelTokenSource) {
    if (token?.cancel && typeof token.cancel === 'function') {
      token.cancel();
    }
  }
  const newCancelTokenSource = getCancelTokenSource();
  cancelTokenSource.push(newCancelTokenSource);
  return invokeRestApi(
    apiKey,
    {
      queryPayload:
        GET_CONVERSATIONS === apiKey
          ? {
              assistantID,
              contextID,
              pageSize,
              pageIndex
            }
          : {
              agentID: assistantID,
              contextID,
              pageSize,
              pageIndex
            },
      cancelTokenSource: newCancelTokenSource
    },
    context
  ).then((response) => {
    cancelTokenSource.pop();
    return response;
  });
};

const processGetMessagesRequest = (
  apiKey: string,
  assistantID: string,
  conversationID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[]
) => {
  // Cancel any ongoing requests from previous CancelTokenSources
  for (const token of cancelTokenSource) {
    if (token?.cancel && typeof token.cancel === 'function') {
      token.cancel();
    }
  }
  const newCancelTokenSource = getCancelTokenSource();
  cancelTokenSource.push(newCancelTokenSource);
  return invokeRestApi(
    apiKey,
    {
      queryPayload:
        GET_MESSAGES === apiKey
          ? {
              assistantID,
              conversationID
            }
          : {
              agentID: assistantID,
              conversationID
            }
    },
    context
  ).then((response) => {
    cancelTokenSource.pop();
    return response;
  });
};

const processCreateConversationRequest = (
  apiKey: string,
  contextID: string,
  assistantID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[]
) => {
  // Cancel any ongoing requests from previous CancelTokenSources
  for (const token of cancelTokenSource) {
    if (token?.cancel && typeof token.cancel === 'function') {
      token.cancel();
    }
  }
  const newCancelTokenSource = getCancelTokenSource();
  cancelTokenSource.push(newCancelTokenSource);
  return invokeRestApi(
    apiKey,
    {
      body: JSON.stringify({ contextID, executeStarterQuestion: false }),
      cancelTokenSource: newCancelTokenSource,
      queryPayload: apiKey === CREATE_CONVERSATION ? { assistantID } : { agentID: assistantID }
    },
    context
  ).then((response: ConversationResponse) => {
    cancelTokenSource.pop();
    return response;
  });
};

const processSendMessageRequest = (
  apiKey: string,
  assistantID: string,
  conversationID: string,
  message: string,
  context: string,
  attachments?: Attachment[],
  enableStreaming?: boolean
) => {
  const payload = {
    ...(message && { request: message }),
    ...(attachments && attachments?.length > 0 && { attachments }),
    ...(enableStreaming && {
      capabilities: {
        stream: {
          isEnabled: true
        }
      }
    })
  };
  return invokeRestApi(
    apiKey,
    {
      body: JSON.stringify(payload),
      queryPayload:
        apiKey === SEND_MESSAGE ? { assistantID, conversationID } : { agentID: assistantID, conversationID },
      ...(enableStreaming && {
        headers: { Accept: 'text/event-stream' },
        adapter: 'fetch',
        responseType: 'stream'
      })
    },
    context
  );
};

/**
 * Obtains a paginated list of conversations for a specific GenAI Assistant and context.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the conversations for the GenAI Assistant with ID Work-Opportunity-Biz!OpportunityAgent in the context with ID UPLUS-PLUS-WORK-OPPORTUNITY OPP-15001.</caption>
 * PCore.getGenAIAssistantUtils().getConversations('Work-Opportunity-Biz!OpportunityAssistant', 'UPLUS-PLUS-WORK-OPPORTUNITY OPP-15001', 10, 1, 'app/primary_1')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  assistantID       The unique identifier of the GenAI Assistant. Note: Ensure that you provide the pxInsName value of the GenAI assistant.
 * @param   {string}  contextID         The unique identifier of the context containing the conversations to be retrieved. NOTE: If the context is a Case, ensure that you provide the pzInsKey value of the Case.
 * @param   {number}  pageSize          The number of conversations to be displayed per page.
 * @param   {number}  pageIndex         The index of the page containing the conversations. Note: Pageindex starts from 1.
 * @param   {string}  context           The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests for obtaining the conversations. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}           A Promise that resolves to an object containing the paginated list of conversations.
 */
export const getConversations = (
  assistantID: string,
  contextID: string,
  pageSize: number,
  pageIndex: number,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processGetConversationsRequest(
    GET_CONVERSATIONS,
    assistantID,
    contextID,
    pageSize,
    pageIndex,
    context,
    cancelTokenSource
  );
};

/**
 * Obtains a paginated list of conversations for a specific Agent and context.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the conversations for the agent with ID Work-Opportunity-Biz!OpportunityAgent in the context with ID WORK-OPPORTUNITY OPP-15001.</caption>
 * PCore.getGenAIAssistantUtils().getConversationsForAgent('Work-Opportunity-Biz!OpportunityAgent', 'WORK-OPPORTUNITY OPP-15001', 10, 1, 'app/primary_1')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  agentID           The unique identifier of the Agent for which the list of conversations must be obtained. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}  contextID         The unique identifier of the context containing the conversations to be obtained. NOTE: If the context is a Case, ensure that you provide the pzInsKey value of the Case.
 * @param   {number}  pageSize          The number of conversations to be displayed per page.
 * @param   {number}  pageIndex         The index of the page containing the conversations. Note: Pageindex starts from 1.
 * @param   {string}  context           The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests for obtaining the conversations.. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}           A Promise that resolves to an object containing the paginated list of conversations.
 */
export const getConversationsForAgent = (
  agentID: string,
  contextID: string,
  pageSize: number,
  pageIndex: number,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processGetConversationsRequest(
    GET_CONVERSATIONS_FOR_AGENT,
    agentID,
    contextID,
    pageSize,
    pageIndex,
    context,
    cancelTokenSource
  );
};

/**
 * Obtains messages from a conversation created for a GenAI Assistant.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the messages from the conversation with the ID CON-38008 for the GenAI Assistant with ID Work-Opportunity-Biz!OpportunityAgent.</caption>
 * PCore.getGenAIAssistantUtils().getMessages('Work-Opportunity-Biz!OpportunityAssistant', 'CON-38008', 'app/primary_1')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  assistantID     The unique identifier of the GenAI Assistant. Note: Ensure that you provide the pxInsName value of the GenAI assistant.
 * @param   {string}  conversationID  The unique identifier of the conversation whose messages need to be retrieved.
 * @param   {string}  context         The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests for obtaining the messages from the conversation. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the messages from the conversation.
 */
export const getMessages = (
  assistantID: string,
  conversationID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processGetMessagesRequest(GET_MESSAGES, assistantID, conversationID, context, cancelTokenSource);
};

/**
 * Obtains messages from a conversation created for an Agent.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the messages from the conversation with the ID CON-38008 for the agent with ID Work-Opportunity-Biz!OpportunityAgent.</caption>
 * PCore.getGenAIAssistantUtils().getMessagesForAgent('Work-Opportunity-Biz!OpportunityAgent', 'CON-38008', 'app/primary_1')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  agentID         The unique identifier of the Agent. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}  conversationID  The unique identifier of the conversation whose messages need to be retrieved.
 * @param   {string}  context         The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests for obtaining the messages from the conversation. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the messages from the conversation.
 */
export const getMessagesForAgent = (
  agentID: string,
  conversationID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processGetMessagesRequest(GET_MESSAGES_FOR_AGENT, agentID, conversationID, context, cancelTokenSource);
};

/**
 * Initiates a conversation for a specified GenAI Assistant.
 * @function
 * @static
 * @example <caption>In this example, the API creates a conversation with a GenAI Assistant for a Case.</caption>
 * PCore.getGenAIAssistantUtils().createConversation(
 * 'UPLUS-PLUS-WORK-OPPORTUNITY OPP-15001',
 * 'UPlus-SAPlusC11n-Work-Opportunity-Biz!OpportunityAssistant',
 * 'app/primary_1',
 * [] // Pass the reference of the array.
 * )
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * })
 * @param   {string}              contextID         The ID of the context where the conversation must be initiated. Note: If the context is a case, ensure that you provide the pzInsKey value of the case.
 * @param   {string}              assistantID       The unique identifier of the GenAI Assistant. Note: Ensure that you provide the pxInsName value of the GenAI assistant.
 * @param   {string}              context           The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests that initiate the conversation. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}     A Promise that resolves to an object containing the details of the conversation with the GenAI Assistant.
 */
export const createConversation = (
  contextID: string,
  assistantID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processCreateConversationRequest(CREATE_CONVERSATION, contextID, assistantID, context, cancelTokenSource);
};

/**
 * Initiates a conversation for a specified Agent.
 * @function
 * @static
 * @example <caption>In this example, the API creates a conversation with an Agent for a Case.</caption>
 * PCore.getGenAIAssistantUtils().createConversationForAgent(
 * 'WORK-OPPORTUNITY OPP-15001',
 * 'Work-Opportunity-Biz!OpportunityAgent',
 * 'app/primary_1',
 * [] // Pass the reference of the array.
 * )
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * })
 * @param   {string}              contextID         The ID of the context where the conversation must be initiated. Note: If the context is a case, ensure that you provide the pzInsKey value of the case.
 * @param   {string}              agentID           The unique identifier of the Agent with which the conversation must be initiated. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}              context           The name of the context where the API is being called.
 * @param   {CancelTokenSource[]} cancelTokenSource The list of tokens corresponding to the API requests that initiate the conversation. Note: `cancelTokenSource` is used to cancel the previous request when a new one is triggered.
 * @returns {Promise<object>}     A Promise that resolves to an object containing the details of the conversation with the Agent.
 */
export const createConversationForAgent = (
  contextID: string,
  agentID: string,
  context: string,
  cancelTokenSource: CancelTokenSource[] = []
) => {
  return processCreateConversationRequest(
    CREATE_CONVERSATION_FOR_AGENT,
    contextID,
    agentID,
    context,
    cancelTokenSource
  );
};

/**
 * Sends a message in an existing conversation with the GenAI Assistant.
 * @function
 * @static
 * @example <caption>In this example, the API sends a message in an existing conversation with a GenAI Assistant.</caption>
 * PCore.getGenAIAssistantUtils().sendMessage('UPlus-SAPlusC11n-Work-Opportunity-Biz!OpportunityAssistant','CON-38008','test message','app/primary_1',{enableStreaming:true})
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  assistantID     The unique identifier of the GenAI Assistant with which the conversation is occurring. Note: Ensure that you provide the pxInsName value of the GenAI assistant.
 * @param   {string}  conversationID  The unique identifier of the object that contains the details of the conversation.
 * @param   {string}  message         The message that must be sent to the assistant.
 * @param   {string}  context         The name of the context where the API is being called.
 * @param   {SendMessageConfig} config The object containing additional configuration options for the message sent to the Pega GenAI Coach.
 * @param   {boolean} config.enableStreaming The flag that determines if the response to the message sent to the Pega GenAI Coach uses server-sent events. Default value is false, if enableStreaming is true, the response from the GenAI Coach is a ReadableStream which means the response is displayed sequentially. If enableStreaming is false, the response from the GenAI Coach is a JSON object.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the response from the GenAI Assistant.
 */
export const sendMessage = (
  assistantID: string,
  conversationID: string,
  message: string,
  context: string,
  config?: SendMessageConfig
) => {
  return processSendMessageRequest(
    SEND_MESSAGE,
    assistantID,
    conversationID,
    message,
    context,
    undefined,
    config?.enableStreaming
  );
};

/**
 * Sends a message in an existing conversation with an Agent.
 * @function
 * @static
 * @example <caption>In this example, the API sends a message in an existing conversation with an Agent.</caption>
 * PCore.getGenAIAssistantUtils().sendMessageForAgent(
 * 'Work-Opportunity-Biz!OpportunityAgent',
 * 'CON-38008',
 * 'test message',
 * 'app/primary_1',
 * [{
 *    category: 'File',
 *    ID: 'b7edfb75-5a9f-4cf3-b282-22e16e9deadf'
 *  }],
 * {enableStreaming:true}
 * ).then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  agentID         The unique identifier of the Agent with which the conversation is occurring. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}  conversationID  The unique identifier of the object that contains the details of the conversation.
 * @param   {string}  message         The message that must be sent to the agent.
 * @param   {string}  context         The name of the context where the API is being called.
 * @param   {Attachment[]} attachments The list of files uploaded to the server that must be sent along with the message.
 *                                    Note: For more information on uploading a file to the server, see uploadAttachment from getAttachmentUtils module
 * @param   {SendMessageConfig} config The object containing additional configuration options for the message sent to the Agent.
 * @param   {boolean} config.enableStreaming The flag that determines if the response to the message sent to the Agent uses server-sent events. Default value is false, if enableStreaming is true, the response from the Agent is a ReadableStream which means the response is displayed sequentially. If enableStreaming is false, the response from the Agent is a JSON object.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the response from the Agent.
 */
export const sendMessageForAgent = (
  agentID: string,
  conversationID: string,
  message: string,
  context: string,
  attachments?: Attachment[],
  config?: SendMessageConfig
) => {
  return processSendMessageRequest(
    SEND_MESSAGE_FOR_AGENT,
    agentID,
    conversationID,
    message,
    context,
    attachments,
    config?.enableStreaming
  );
};

/**
 * Likes a message in a conversation with an Agent.
 * @function
 * @static
 * @example <caption>In this example, the API likes a message with ID M-100 in a conversation with an Agent.</caption>
 * PCore.getGenAIAssistantUtils().likeAgentMessage('Work-Opportunity-Biz!OpportunityAgent','CON-38008','M-100','app/primary_1')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  agentID         The unique identifier of the Agent with which the conversation is occurring. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}  conversationID  The unique identifier of the object that contains the details of the conversation.
 * @param   {string}  messageID       The unique identifier of the message that is being liked.
 * @param   {string}  context         The name of the context where the API is being called.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the response from the Agent.
 */
export const likeAgentMessage = (agentID: string, conversationID: string, messageID: string, context: string) => {
  const requestPayload: {
    queryPayload: { assistantID?: string; agentID?: string; conversationID: string; messageID: string };
    body?: string;
  } = {
    queryPayload: { agentID, conversationID, messageID }
  };

  return invokeRestApi(LIKE_AGENT_MESSAGE, requestPayload, context);
};

/**
 * Unlikes a message in a conversation with an Agent.
 * @function
 * @static
 * @example <caption>In this example, the API unlikes a message with ID M-100 in a conversation with an Agent.</caption>
 * PCore.getGenAIAssistantUtils().unLikeAgentMessage('Work-Opportunity-Biz!OpportunityAgent','CON-38008','M-100','app/primary_1','feedback text')
 * .then(response => {
 *    // response
 * }).catch(err => {
 *   // errors
 * });
 * @param   {string}  agentID         The unique identifier of the Agent with which the conversation is occurring. Note: Ensure that you provide the pxInsName value of the agent.
 * @param   {string}  conversationID  The unique identifier of the object that contains the details of the conversation.
 * @param   {string}  messageID       The unique identifier of the message that is being disliked.
 * @param   {string}  context         The name of the context where the API is being called.
 * @param   {string}  feedbackText    The feedback provided for the unliked message. Note: The default value is empty string.
 * @returns {Promise<object>}         A Promise that resolves to an object containing the response from the Agent.
 */
export const unLikeAgentMessage = (
  agentID: string,
  conversationID: string,
  messageID: string,
  context: string,
  feedbackText = ''
) => {
  const requestPayload = {
    queryPayload: { agentID, conversationID, messageID },
    body: JSON.stringify({ feedbackText })
  };

  return invokeRestApi(UNLIKE_AGENT_MESSAGE, requestPayload, context);
};

export default {
  createConversation,
  sendMessage,
  createConversationForAgent,
  sendMessageForAgent,
  likeAgentMessage,
  unLikeAgentMessage,
  getConversations,
  getConversationsForAgent,
  getMessages,
  getMessagesForAgent
};
